/**
 * LegalReacceptanceModal — blocking modal shown after admin publishes
 * an updated Terms or Privacy.
 *
 * UX (mandatory):
 *   • Cannot be dismissed by clicking outside.
 *   • No X close button.
 *   • Escape key disabled.
 *   • Two paths forward:
 *       [I Accept and Continue]  → POST /legal/me/accept, then close.
 *       [Log Out]                → drop the session.
 *   • Document viewers are NESTED LegalDocModal instances — open inside
 *     this modal, return here on close.
 *
 * Mount globally (App.js) — it checks /legal/me/status on mount and
 * stays hidden if the user is up to date.
 */
import { useEffect, useState } from 'react';
import { FileText, Shield, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import LegalDocModal from './LegalDocModal';

export default function LegalReacceptanceModal({ status, onAccepted }) {
  const auth = useAuth?.() || {};
  const logout = auth.logout;
  const [openDoc, setOpenDoc] = useState(null); // 'terms' | 'privacy' | null
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  // Block body scroll + Escape key while mounted.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      // Escape would normally close — explicitly disabled.
      if (e.key === 'Escape') e.preventDefault();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey, true);
    };
  }, []);

  const accept = async () => {
    setAccepting(true);
    setError(null);
    try {
      await axios.post(`${API}/legal/me/accept`);
      onAccepted?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to record acceptance. Try again.');
    } finally {
      setAccepting(false);
    }
  };

  const effectiveDate = (
    status?.terms?.effective_date || status?.privacy?.effective_date
  );
  const dateStr = effectiveDate
    ? new Date(effectiveDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : 'today';

  return (
    <>
      {/* If a nested document modal is open, render it on top.
          We INTENTIONALLY do NOT pass onUnderstand → onClose returns
          the user to this re-acceptance modal. */}
      {openDoc && (
        <LegalDocModal
          documentType={openDoc}
          onClose={() => setOpenDoc(null)}
          onUnderstand={() => setOpenDoc(null)}
        />
      )}

      <div
        className="fixed inset-0 z-[9990] flex items-center justify-center bg-black/70 backdrop-blur-sm"
        data-testid="legal-reacceptance-modal"
      >
        <div
          className="bg-white w-[480px] max-w-[92vw] rounded-2xl shadow-2xl p-6 sm:p-7"
          role="alertdialog"
          aria-modal="true"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
              <AlertCircle className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-gray-900">
                Legal updates require your acceptance
              </h2>
              <p className="text-[12px] text-gray-500 mt-1 leading-relaxed">
                Unexa has updated its Terms and Conditions and Privacy Policy.
                Effective {dateStr}.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 my-5">
            <button
              onClick={() => setOpenDoc('terms')}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700 transition-colors"
              data-testid="reaccept-view-terms-btn"
            >
              <FileText className="h-3.5 w-3.5" /> View Terms
            </button>
            <button
              onClick={() => setOpenDoc('privacy')}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-xs font-semibold text-gray-700 transition-colors"
              data-testid="reaccept-view-privacy-btn"
            >
              <Shield className="h-3.5 w-3.5" /> View Privacy
            </button>
          </div>

          {error && (
            <div className="mb-3 text-[12px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <button
              onClick={accept}
              disabled={accepting}
              className="w-full px-4 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-bold flex items-center justify-center gap-2 transition-colors"
              data-testid="reaccept-accept-btn"
            >
              {accepting && <Loader2 className="h-4 w-4 animate-spin" />}
              I Accept and Continue
            </button>
            <button
              onClick={() => { try { logout?.(); } catch { /* noop */ } }}
              className="w-full px-4 py-2.5 text-xs font-semibold text-gray-500 hover:text-gray-700"
              data-testid="reaccept-logout-btn"
            >
              Log Out
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
